import { useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

/**
 * Minutes the SAS stays valid. The route applies this as
 * `timedelta(minutes=expiry)`, so 1440 is 24 hours — the same value
 * my-pymble-portal's useAzureStorage asks for.
 *
 * (The endpoint's own docstring says "seconds"; the implementation says
 * minutes. Minutes is what actually happens.)
 */
const SAS_EXPIRY_MINUTES = 1440;

const SAS_END_POINT = `storage/sas/?container=profile-images&expiry=${SAS_EXPIRY_MINUTES}`;

interface SasResponse {
	ContainerUrl?: string;
	SasExpiry?: string;
	SasQueryString?: string;
}

/**
 * Builds URLs for contact profile photos.
 *
 * Photos live in the private `profile-images` blob container as
 * `{contactId}.jpg`, so every request needs a shared access signature. One SAS
 * covers the whole container, so it is fetched once and reused for every row
 * rather than per person.
 *
 * Returns `undefined` when there is no token yet or no contact id, which is the
 * signal for CCAvatar to draw initials instead. A missing photo needs no
 * special handling either: CCAvatar falls back to initials on image error, and
 * most contacts have no photo on file.
 */
export function useProfileImages() {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const { data } = useQuery<SasResponse | null>({
		queryKey: ["storage", "sas", "profile-images"],
		queryFn: async () => callPymbleApi(instance, "GET", SAS_END_POINT),
		enabled: isAuthenticated,
		// Refresh at half the token's life so an open tab never starts serving
		// URLs signed with a lapsed signature.
		staleTime: (SAS_EXPIRY_MINUTES / 2) * 60 * 1000,
		retry: false,
	});

	const imageUrl = useCallback(
		(contactId: string | undefined): string | undefined => {
			if (!contactId || !data?.ContainerUrl || !data?.SasQueryString) {
				return undefined;
			}
			return `${data.ContainerUrl}/${contactId}.jpg?${data.SasQueryString}`;
		},
		[data]
	);

	return { imageUrl };
}
